import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
  BarChart,
  Bar,
  Cell,
} from 'recharts'
import type { JournalEntry } from '../lib/supabase'

interface Props {
  entries: JournalEntry[]
}

const EMOTION_COLORS: Record<string, string> = {
  calm: '#00c853',
  confident: '#00d4ff',
  neutral: '#5f6368',
  anxious: '#ffab00',
  fearful: '#ff1744',
  greedy: '#ff9100',
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div style={{
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border-medium)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-sm) var(--space-md)',
        fontFamily: 'var(--font-mono)',
        fontSize: '0.75rem',
      }}>
        <div style={{ color: 'var(--text-muted)' }}>{label}</div>
        {payload.map((p: any) => (
          <div key={p.name} style={{ color: p.color || 'var(--text-primary)' }}>
            {p.name}: {typeof p.value === 'number' ? p.value.toFixed(1) : p.value}
          </div>
        ))}
      </div>
    )
  }
  return null
}

export function JournalAnalytics({ entries }: Props) {
  const tradeEntries = entries.filter(e => e.entry_type === 'trade_review')

  // Summary stats
  const totalEntries = entries.length
  const wins = tradeEntries.filter(e => e.outcome === 'win').length
  const losses = tradeEntries.filter(e => e.outcome === 'loss').length
  const winRate = tradeEntries.length > 0 ? (wins / tradeEntries.length * 100) : 0
  const avgRuleAdherence = tradeEntries.filter(e => e.rule_adherence !== null).length > 0
    ? tradeEntries.filter(e => e.rule_adherence !== null).reduce((sum, e) => sum + (e.rule_adherence || 0), 0) / tradeEntries.filter(e => e.rule_adherence !== null).length
    : 0

  // Rule adherence trend (last N entries, chronological)
  const adherenceTrend = [...tradeEntries]
    .filter(e => e.rule_adherence !== null)
    .reverse()
    .map(e => ({
      date: e.trade_date,
      adherence: e.rule_adherence || 0,
      ticker: e.ticker || '',
    }))

  // Emotion distribution
  const emotionCounts: Record<string, number> = {}
  entries.forEach(e => {
    if (e.emotion_before) emotionCounts[e.emotion_before] = (emotionCounts[e.emotion_before] || 0) + 1
  })
  const emotionData = Object.entries(emotionCounts)
    .map(([emotion, count]) => ({ emotion, count }))
    .sort((a, b) => b.count - a.count)

  // Setup quality vs outcome
  const qualityOutcome = tradeEntries
    .filter(e => e.setup_quality !== null && e.outcome)
    .reduce((acc, e) => {
      const key = e.setup_quality!
      if (!acc[key]) acc[key] = { quality: key, wins: 0, losses: 0 }
      if (e.outcome === 'win') acc[key].wins++
      else if (e.outcome === 'loss') acc[key].losses++
      return acc
    }, {} as Record<number, { quality: number; wins: number; losses: number }>)
  const qualityData = Object.values(qualityOutcome).sort((a, b) => a.quality - b.quality)

  return (
    <div>
      {/* Summary Stats */}
      <div className="journal-stats">
        <div className="journal-stat">
          <div className="journal-stat-value">{totalEntries}</div>
          <div className="journal-stat-label">Entries</div>
        </div>
        <div className="journal-stat">
          <div className="journal-stat-value" style={{ color: winRate >= 50 ? 'var(--positive)' : 'var(--negative)' }}>
            {winRate.toFixed(0)}%
          </div>
          <div className="journal-stat-label">Win Rate</div>
        </div>
        <div className="journal-stat">
          <div className="journal-stat-value" style={{ color: 'var(--accent-cyan)' }}>
            {avgRuleAdherence.toFixed(1)}
          </div>
          <div className="journal-stat-label">Avg Adherence</div>
        </div>
        <div className="journal-stat">
          <div className="journal-stat-value">
            {wins}<span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>W</span> {losses}<span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>L</span>
          </div>
          <div className="journal-stat-label">Record</div>
        </div>
      </div>

      {/* Rule Adherence Trend */}
      {adherenceTrend.length > 0 && (
        <div style={{ marginBottom: 'var(--space-xl)' }}>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 'var(--space-sm)' }}>
            Rule Adherence Trend
          </div>
          <div style={{ height: 140 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={adherenceTrend} margin={{ top: 5, right: 5, left: 5, bottom: 0 }}>
                <defs>
                  <linearGradient id="adherenceGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--accent-cyan)" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="var(--accent-cyan)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" vertical={false} />
                <XAxis dataKey="ticker" axisLine={false} tickLine={false} tick={{ fill: 'var(--text-muted)', fontSize: 10 }} />
                <YAxis domain={[0, 5]} axisLine={false} tickLine={false} tick={{ fill: 'var(--text-muted)', fontSize: 10 }} width={20} />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="adherence"
                  stroke="var(--accent-cyan)"
                  strokeWidth={2}
                  fill="url(#adherenceGradient)"
                  dot={{ r: 3, fill: 'var(--accent-cyan)', stroke: 'var(--bg-card)', strokeWidth: 2 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Emotion Distribution */}
      {emotionData.length > 0 && (
        <div style={{ marginBottom: 'var(--space-xl)' }}>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 'var(--space-sm)' }}>
            Emotion Distribution (Pre-trade)
          </div>
          <div style={{ height: 120 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={emotionData} margin={{ top: 5, right: 5, left: 5, bottom: 0 }}>
                <XAxis dataKey="emotion" axisLine={false} tickLine={false} tick={{ fill: 'var(--text-muted)', fontSize: 10 }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: 'var(--text-muted)', fontSize: 10 }} width={20} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {emotionData.map((d) => (
                    <Cell key={d.emotion} fill={EMOTION_COLORS[d.emotion] || 'var(--text-muted)'} fillOpacity={0.7} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Setup Quality vs Outcome */}
      {qualityData.length > 0 && (
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 'var(--space-sm)' }}>
            Setup Quality vs Outcome
          </div>
          <div style={{ height: 120 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={qualityData} margin={{ top: 5, right: 5, left: 5, bottom: 0 }}>
                <XAxis dataKey="quality" axisLine={false} tickLine={false} tick={{ fill: 'var(--text-muted)', fontSize: 10 }} tickFormatter={v => `Q${v}`} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: 'var(--text-muted)', fontSize: 10 }} width={20} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="wins" stackId="a" fill="var(--positive)" fillOpacity={0.7} radius={[0, 0, 0, 0]} />
                <Bar dataKey="losses" stackId="a" fill="var(--negative)" fillOpacity={0.7} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  )
}
